import { Controller, useFormState } from 'react-hook-form';
import { ICommonFormProps } from '@src/components/DynamicForm';
import { FormLabel, MultiSelect, SingleSelect } from '@src/components/ui';
import { useMemo } from 'react';
import { useGetTenantCountryListQuery } from '@src/services/b2c/country/countryList/countryListApiSlice';

export const FormSelectTenantCountry = (props: Readonly<ICommonFormProps>) => {
    const {
        label,
        keyName,
        control,
        className,
        registerOptions,
        formProps,
        inputProperties,
    } = props;

    const { data, isLoading, isFetching } = useGetTenantCountryListQuery({});

    const options = useMemo(() => {
        return (
            data?.data
                ?.map((item) => ({
                    label: item.name,
                    value: item.id,
                }))
                .sort((a, b) => a.label.localeCompare(b.label)) || []
        );
    }, [data]);

    const { touchedFields, isSubmitted } = useFormState({ control });

    return (
        <div className={className}>
            <Controller
                control={control}
                name={keyName}
                render={({ field, fieldState: { error: fieldStateError } }) => {
                    // Use filtered errors prop if available, otherwise use fieldState error
                    // But only show if field has been touched or form submitted
                    const error = props.errors ?? fieldStateError;
                    const showError = Boolean(
                        error && (touchedFields[keyName] || isSubmitted)
                    );
                    const handleChange = (val: any) => {
                        if (formProps?.isMulti) {
                            field.onChange(val ?? []);
                        } else {
                            field.onChange(val ?? null);
                        }
                    };
                    return (
                        <>
                            {label && (
                                <FormLabel
                                    label={label}
                                    required={
                                        typeof registerOptions?.required ===
                                        'object'
                                            ? Boolean(
                                                  registerOptions.required.value
                                              )
                                            : Boolean(registerOptions?.required)
                                    }
                                />
                            )}
                            {formProps?.isMulti ? (
                                <MultiSelect
                                    field={{ ...field, onChange: handleChange }}
                                    options={options}
                                    loading={isLoading || isFetching}
                                    placeholder={
                                        inputProperties?.placeholder ??
                                        'Select countries'
                                    }
                                    allowClear={formProps?.allowClear}
                                />
                            ) : (
                                <SingleSelect
                                    field={{ ...field, onChange: handleChange }}
                                    options={options}
                                    loading={isLoading || isFetching}
                                    placeholder={
                                        inputProperties?.placeholder ??
                                        'Select a country'
                                    }
                                    allowClear={formProps?.allowClear}
                                    filterOption={
                                        formProps?.filterOption ?? false
                                    }
                                />
                            )}
                            {showError && error?.message && (
                                <div className="text-red-500 text-sm mt-0">
                                    {error.message}
                                </div>
                            )}
                        </>
                    );
                }}
            />
        </div>
    );
};
